import { TextField, useFormContext, useFormField } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { BareButton } from '@calendly/ui/components/bare-button'
import { Button } from '@calendly/ui/components/button'
import { IconButton } from '@calendly/ui/components/icon-button'
import { DeleteIcon, DragIcon } from '@calendly/ui/components/icons'
import { SwitchInput } from '@calendly/ui/components/switch-input'
import { BodyText, LabelText } from '@calendly/ui/components/typography'
import { colorIconSecondary } from '@calendly/ui/theme'
import { useCallback } from 'react'

import { useAdvancedSortable } from 'components/core/sortable'
import { UrlField } from 'components/event_type_edit'
import { useErrorAfterInitialTouch } from 'features/event_type_editor'

import {
  CardContent,
  CollapsedCard,
  DragHandle,
  ExpandedCard,
  ExpandedCardBody,
  ExpandedCardFooter,
  ExpandedCardHeader,
  SwitchToggleLabel,
  linkCardContentCss,
} from './styles'
import { validateEditingFields } from './validate_editing_fields'

export const CustomLinkCard = ({
  disabled = false,
  editingKey,
  index,
  link,
  onCancel,
  onDelete,
  onDone,
  onEdit,
}) => {
  const {
    setFieldTouched,
    validateForm,
  } = useFormContext()
  const contentFieldName = `customLinks.${index}.content`
  const urlFieldName = `customLinks.${index}.url`
  const enabledFieldName = `customLinks.${index}.enabled`
  const [{ value: enabled }, _, { setValue: setEnabled }] = useFormField({ name: enabledFieldName })
  const isEditing = editingKey === link.id
  const isNewLink = String(link.id).startsWith('new')
  const displayLabel = link.content || I18n.t('publisher.event_type_editor.sections.booking_page_options.confirmation_page.new_link')

  const {
    sortable: {
      isDragging,
      attributes,
      listeners,
      setNodeRef,
    },
    itemStyle,
  } = useAdvancedSortable({ id: link.id })

  useErrorAfterInitialTouch(contentFieldName)
  useErrorAfterInitialTouch(urlFieldName)

  const handleDone = useCallback(async () => {
    const hasErrors = await validateEditingFields({
      fieldNames: [contentFieldName, urlFieldName],
      setFieldTouched,
      validateForm,
    })

    if (!hasErrors) {
      onDone()
    }
  }, [contentFieldName, onDone, setFieldTouched, urlFieldName, validateForm])

  if (isEditing) {
    return (
      <ExpandedCard
        ref={setNodeRef}
        data-testid={`custom-link-card-editing-${index}`}
        isDragging={isDragging}
        {...itemStyle}
      >
        <ExpandedCardHeader>
          <DragHandle {...attributes} {...listeners}>
            <DragIcon color={colorIconSecondary} size="standard" title="" />
          </DragHandle>
          <CardContent>
            <LabelText color="muted">
              {I18n.t('publisher.event_type_editor.sections.booking_page_options.confirmation_page.new_link')}
            </LabelText>
          </CardContent>
        </ExpandedCardHeader>
        <ExpandedCardBody>
          <TextField
            autoFocus
            aria-label={I18n.t('publisher.event_type_editor.sections.booking_page_options.confirmation_page.custom_link.link_name')}
            label={I18n.t('publisher.event_type_editor.sections.booking_page_options.confirmation_page.custom_link.link_name')}
            name={contentFieldName}
            placeholder={I18n.t('publisher.event_type_editor.sections.booking_page_options.confirmation_page.custom_link.link_name')}
            required
          />
          <UrlField
            aria-label={I18n.t('publisher.event_type_editor.sections.booking_page_options.confirmation_page.custom_link.link_url')}
            label={I18n.t('publisher.event_type_editor.sections.booking_page_options.confirmation_page.custom_link.link_url')}
            name={urlFieldName}
            placeholder="https://www.example.com"
            required
          />
          <div>
            <BodyText size="medium">
              {I18n.t('publisher.event_type_editor_crud.confirmation.status_label')}
            </BodyText>
            <SwitchToggleLabel>
              <BodyText size="medium">
                {enabled
                  ? I18n.t('publisher.action.on')
                  : I18n.t('publisher.action.off')}
              </BodyText>
              <SwitchInput
                aria-label={I18n.t('publisher.event_type_editor_crud.confirmation.status_label')}
                checked={enabled}
                name={enabledFieldName}
                onChange={() => setEnabled(!enabled)}
              />
            </SwitchToggleLabel>
          </div>
        </ExpandedCardBody>
        <ExpandedCardFooter>
          {isNewLink && (
            <Button appearance="ghost" size="small" type="button" onClick={onDelete}>
              {I18n.t('publisher.action.delete')}
            </Button>
          )}
          <Button appearance="ghost" size="small" type="button" onClick={onCancel}>
            {I18n.t('publisher.action.cancel')}
          </Button>
          <Button appearance="primary" size="small" type="button" onClick={handleDone}>
            {I18n.t('publisher.action.done')}
          </Button>
        </ExpandedCardFooter>
      </ExpandedCard>
    )
  }

  return (
    <CollapsedCard
      ref={setNodeRef}
      data-testid={`custom-link-card-${index}`}
      isDragging={isDragging}
      {...itemStyle}
    >
      <DragHandle {...attributes} {...listeners}>
        <DragIcon color={colorIconSecondary} size="standard" title="" />
      </DragHandle>
      <BareButton
        aria-label={I18n.t('publisher.event_type_editor_crud.confirmation.customize')}
        className={linkCardContentCss}
        disabled={disabled}
        onClick={onEdit}
      >
        <LabelText color={link.content ? 'standard' : 'muted'}>{displayLabel}</LabelText>
        {link.url && (
          <BodyText color="muted" size="small">
            {link.url}
          </BodyText>
        )}
      </BareButton>
      <IconButton
        aria-label={I18n.t('publisher.action.delete')}
        disabled={disabled}
        size="small"
        title={I18n.t('publisher.action.delete')}
        onClick={onDelete}
      >
        <DeleteIcon />
      </IconButton>
    </CollapsedCard>
  )
}
